import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// Create axios instance
const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Enable sending cookies with requests
});

// Add auth token to requests (for backward compatibility)
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('tour_finder_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Don't redirect if already on login page or if this is a /auth/me check
      const isLoginPage = window.location.pathname === '/login';
      const isAuthMeCheck = error.config?.url?.includes('/auth/me');
      
      if (!isLoginPage && !isAuthMeCheck) {
        localStorage.removeItem('tour_finder_token');
        localStorage.removeItem('tour_finder_user');
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  logout: () => api.post('/auth/logout'),
  getMe: () => api.get('/auth/me'),
};

// Tours API
export const toursAPI = {
  getAll: (params) => api.get('/tours', { params }),
  getById: (id) => api.get(`/tours/${id}`),
  getAvailability: (id, params) => api.get(`/tours/${id}/availability`, { params }),
  getAvailabilitySlots: (id, params) => api.get(`/tours/${id}/availability-slots`, { params }),
  searchSuggestions: (q) => api.get('/tours/search/suggestions', { params: { q } }),
  getFeatured: () => api.get('/tours/featured'),
};

// Badges API
export const badgesAPI = {
  getAll: () => api.get('/badges'),
};

// Quick Searches API
export const quickSearchesAPI = {
  getAll: () => api.get('/quick-searches'),
};

// Categories API (Public)
export const categoriesAPI = {
  getAll: () => api.get('/categories'),
};

// Reviews API
export const reviewsAPI = {
  getByTour: (tourId) => api.get(`/tours/${tourId}/reviews`),
  create: (tourId, data) => api.post(`/tours/${tourId}/reviews`, data),
};

// Bookings API
export const bookingsAPI = {
  create: (data) => api.post('/bookings', data),
  getAll: () => api.get('/bookings'),
  getById: (id) => api.get(`/bookings/${id}`),
  cancel: (id) => api.delete(`/bookings/${id}`),
};

// Payments API
export const paymentsAPI = {
  createCheckout: (data) => api.post('/payments/checkout', data),
  getStatus: (sessionId) => api.get(`/payments/status/${sessionId}`),
};

// Admin API
export const adminAPI = {
  // Tours
  getAllTours: () => api.get('/admin/tours'),
  createTour: (data) => api.post('/admin/tours', data),
  updateTour: (id, data) => api.put(`/admin/tours/${id}`, data),
  deleteTour: (id) => api.delete(`/admin/tours/${id}`),
  
  // Tour Availability
  getTourAvailability: (tourId) => api.get(`/admin/tours/${tourId}/availability`),
  createAvailabilitySlot: (tourId, data) => api.post(`/admin/tours/${tourId}/availability`, data),
  createBulkAvailability: (tourId, data) => api.post(`/admin/tours/${tourId}/availability/bulk`, data),
  updateAvailabilitySlot: (tourId, slotId, data) => api.put(`/admin/tours/${tourId}/availability/${slotId}`, data),
  deleteAvailabilitySlot: (tourId, slotId) => api.delete(`/admin/tours/${tourId}/availability/${slotId}`),
  blockTourDate: (tourId, date) => api.post(`/admin/tours/${tourId}/availability/block`, { date }),
  unblockTourDate: (tourId, date) => api.post(`/admin/tours/${tourId}/availability/unblock`, { date }),
  updateTourAvailabilitySettings: (tourId, settings) => api.put(`/admin/tours/${tourId}/availability/settings`, settings),
  
  // Bookings
  getAllBookings: () => api.get('/admin/bookings'),
  getBooking: (id) => api.get(`/admin/bookings/${id}`),
  updateBookingStatus: (id, status) => api.put(`/admin/bookings/${id}/status?status=${status}`),
  updatePaymentStatus: (id, status) => api.put(`/admin/bookings/${id}/payment-status?status=${status}`),
  updateBooking: (id, data) => api.put(`/admin/bookings/${id}`, data),
  deleteBooking: (id) => api.delete(`/admin/bookings/${id}`),
  markBookingViewed: (id) => api.put(`/admin/bookings/${id}/viewed`),
  markAllBookingsViewed: () => api.put('/admin/bookings/mark-all-viewed'),
  getBookingReceiptUrl: (bookingId) => `${API_BASE}/bookings/${bookingId}/receipt`,
  downloadReceipt: (bookingId) => api.get(`/bookings/${bookingId}/receipt`, { responseType: 'blob' }),
  
  // Hotels (uses pickup-hotels endpoint)
  getHotels: () => api.get('/admin/pickup-hotels'),
  
  // Stats
  getStats: () => api.get('/admin/stats'),
  
  // Settings
  getSettings: () => api.get('/admin/settings'),
  updateSettings: (data) => api.put('/admin/settings', data),
  
  // Message Logs (Messaging Center)
  getMessageLogs: () => api.get('/admin/message-logs'),
  logMessage: (data) => api.post('/admin/message-logs', data),
  
  // Reviews
  getTourReviews: (tourId) => api.get(`/admin/tours/${tourId}/reviews`),
  deleteReview: (reviewId) => api.delete(`/admin/reviews/${reviewId}`),
  
  // Categories
  getAllCategories: () => api.get('/admin/categories'),
  createCategory: (data) => api.post('/admin/categories', data),
  updateCategory: (id, data) => api.put(`/admin/categories/${id}`, data),
  deleteCategory: (id) => api.delete(`/admin/categories/${id}`),
  toggleCategory: (id) => api.patch(`/admin/categories/${id}/toggle`),
  reorderCategories: (ids) => api.post('/admin/categories/reorder', ids),
  
  // Badges
  getAllBadges: () => api.get('/admin/badges'),
  createBadge: (data) => api.post('/admin/badges', data),
  updateBadge: (id, data) => api.put(`/admin/badges/${id}`, data),
  deleteBadge: (id) => api.delete(`/admin/badges/${id}`),
  
  // Quick Searches
  getAllQuickSearches: () => api.get('/admin/quick-searches'),
  createQuickSearch: (data) => api.post('/admin/quick-searches', data),
  updateQuickSearch: (id, data) => api.put(`/admin/quick-searches/${id}`, data),
  deleteQuickSearch: (id) => api.delete(`/admin/quick-searches/${id}`),
  
  // POS Payments
  getAllPOSPayments: () => api.get('/admin/pos-payments'),
  createPOSPayment: (data) => api.post('/admin/pos-payments', data),
  getPOSPayment: (id) => api.get(`/admin/pos-payments/${id}`),
  checkPOSPaymentStatus: (id) => api.get(`/admin/pos-payments/${id}/status`),
  
  // Invoices
  getAllInvoices: () => api.get('/admin/invoices'),
  createInvoice: (data) => api.post('/admin/invoices', data),
  getInvoice: (id) => api.get(`/admin/invoices/${id}`),
  updateInvoice: (id, data) => api.put(`/admin/invoices/${id}`, data),
  deleteInvoice: (id) => api.delete(`/admin/invoices/${id}`),
  sendInvoice: (id) => api.post(`/admin/invoices/${id}/send`),
  sendInvoiceViaSMS: (id) => api.post(`/admin/invoices/${id}/send-sms`),
  
  // Payment History
  getPaymentHistory: () => api.get('/admin/payment-history'),
  
  // Email Templates
  getAllEmailTemplates: () => api.get('/admin/email-templates'),
  getEmailTemplate: (type) => api.get(`/admin/email-templates/${type}`),
  updateEmailTemplate: (type, data) => api.put(`/admin/email-templates/${type}`, data),
  previewEmailTemplate: (type) => api.post(`/admin/email-templates/${type}/preview`),
  
  // SMS Templates
  getAllSMSTemplates: () => api.get('/admin/sms-templates'),
  getSMSTemplate: (type) => api.get(`/admin/sms-templates/${type}`),
  updateSMSTemplate: (type, data) => api.put(`/admin/sms-templates/${type}`, data),
  toggleSMSTemplate: (type) => api.patch(`/admin/sms-templates/${type}/toggle`),
  previewSMSTemplate: (type) => api.post(`/admin/sms-templates/${type}/preview`),
  getSMSLogs: () => api.get('/admin/sms-logs'),
  sendTestSMS: (phone) => api.post(`/admin/sms/test?phone_number=${encodeURIComponent(phone)}`),
  sendCustomSMS: (data) => api.post('/admin/sms/send', data),
  
  // Custom Message Templates (user-created)
  getCustomTemplates: () => api.get('/admin/message-templates'),
  createCustomTemplate: (data) => api.post('/admin/message-templates', data),
  updateCustomTemplate: (id, data) => api.put(`/admin/message-templates/${id}`, data),
  deleteCustomTemplate: (id) => api.delete(`/admin/message-templates/${id}`),
  bulkImportTemplates: (data) => api.post('/admin/message-templates/bulk-import', data),
  exportTemplates: () => api.get('/admin/message-templates/export'),
  
  // Email Logs & Customer Communication
  getEmailLogs: () => api.get('/admin/email-logs'),
  sendCustomerEmail: (data) => api.post('/admin/send-customer-email', data),
  sendCommunication: (endpoint, data) => api.post(endpoint, data),
  
  // Email Configuration & Testing
  getEmailConfig: () => api.get('/admin/email/config'),
  sendTestEmail: (email) => api.post('/admin/email/test', { email }),
  
  // Email Inbox (Inbound Emails)
  getEmailInbox: () => api.get('/admin/emails/inbox'),
  markEmailRead: (emailId) => api.put(`/admin/emails/${emailId}/read`),
  
  // Unified Communications Inbox
  getCommunicationsInbox: (channel = null, limit = 50) => 
    api.get(`/admin/communications/inbox${channel ? `?channel=${channel}&limit=${limit}` : `?limit=${limit}`}`),
  getDeliveryReports: (status = null, limit = 100) => 
    api.get(`/admin/communications/delivery-reports${status ? `?status=${status}&limit=${limit}` : `?limit=${limit}`}`),
  
  // SMS Inbox
  getSmsInbox: (limit = 50) => api.get(`/admin/sms/inbox?limit=${limit}`),
  markSmsRead: (smsId) => api.put(`/admin/sms/${smsId}/read`),
  replyToSms: (smsId, message) => api.post(`/admin/sms/reply/${smsId}`, { message }),
  
  // WhatsApp Inbox
  getWhatsappInbox: (limit = 50) => api.get(`/admin/whatsapp/inbox?limit=${limit}`),
  sendWhatsapp: (phoneNumber, message) => api.post('/admin/whatsapp/send', { phone_number: phoneNumber, message }),
  replyToWhatsapp: (messageId, message) => api.post(`/admin/whatsapp/reply/${messageId}`, { message }),
  
  // Admin Notifications
  getNotifications: (unreadOnly = false) => api.get(`/admin/notifications${unreadOnly ? '?unread_only=true' : ''}`),
  getUnreadNotificationCount: () => api.get('/admin/notifications/count'),
  markNotificationRead: (notificationId) => api.put(`/admin/notifications/${notificationId}/read`),
  markAllNotificationsRead: () => api.put('/admin/notifications/mark-all-read'),
  
  // Reminders
  getPendingReminders: () => api.get('/admin/reminders/pending'),
  sendUpcomingReminders: () => api.post('/admin/reminders/send-upcoming'),
  getReminderLogs: () => api.get('/admin/reminders/logs'),
  
  // Waiver Management
  sendWaiverReminder: (bookingId, data) => api.post(`/admin/bookings/${bookingId}/send-waiver-reminder`, data),
  updateBookingGuest: (bookingId, data) => api.put(`/admin/bookings/${bookingId}/guest`, data),
  
  // Image Upload
  uploadImage: async (file) => {
    // Convert file to base64
    const reader = new FileReader();
    const base64Promise = new Promise((resolve, reject) => {
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
    });
    reader.readAsDataURL(file);
    const base64Data = await base64Promise;
    
    // Upload to server
    const response = await api.post('/upload/image', { image_data: base64Data });
    return { data: { url: `${BACKEND_URL}${response.data.url}` } };
  },
  
  // User Management
  getAllUsers: () => api.get('/admin/users'),
  getUser: (id) => api.get(`/admin/users/${id}`),
  createUser: (data) => api.post('/admin/users', data),
  updateUser: (id, data) => api.put(`/admin/users/${id}`, data),
  deleteUser: (id) => api.delete(`/admin/users/${id}`),
  changeUserPassword: (id, data) => api.put(`/admin/users/${id}/password`, data),
  
  // Pickup Hotels
  getPickupHotels: () => api.get('/admin/pickup-hotels'),
  createPickupHotel: (data) => api.post('/admin/pickup-hotels', data),
  updatePickupHotel: (id, data) => api.put(`/admin/pickup-hotels/${id}`, data),
  deletePickupHotel: (id) => api.delete(`/admin/pickup-hotels/${id}`),
  
  // Waiver
  getWaiver: () => api.get('/admin/waiver'),
  updateWaiver: (data) => api.put('/admin/waiver', data),
  
  // Waiver Templates Management
  getWaiverTemplates: () => api.get('/admin/waiver-templates'),
  getWaiverTemplatesByType: (type) => api.get(`/admin/waiver-templates/${type}`),
  getWaiverTemplate: (id) => api.get(`/admin/waiver-template/${id}`),
  createWaiverTemplate: (data) => api.post('/admin/waiver-templates', data),
  updateWaiverTemplate: (id, data) => api.put(`/admin/waiver-templates/${id}`, data),
  deleteWaiverTemplate: (id) => api.delete(`/admin/waiver-templates/${id}`),
  
  // Waiver History & Signatures
  getWaiverHistory: (params) => api.get('/admin/waivers/history', { params }),
  getBookingWaivers: (bookingId) => api.get(`/admin/waivers/booking/${bookingId}`),
  exportWaivers: (params) => api.get('/admin/waivers/export', { params }),
  
  // Send Waiver Reminders
  sendWaiverReminderAll: (bookingId, data) => api.post(`/admin/bookings/${bookingId}/send-waiver-reminder-all`, data),
  
  // Communication - Email & SMS
  sendEmail: (data) => api.post('/admin/communications/send-email', data),
  sendSms: (data) => api.post('/admin/communications/send-sms', data),
  
  // Refunds
  processRefund: (data) => api.post('/admin/refunds/process', data),
  getRefundHistory: (bookingId) => api.get(`/admin/refunds/${bookingId}`),
  getRefundLogs: () => api.get('/admin/refunds'),
  
  // Cancellations
  cancelBooking: (bookingId, data) => api.post(`/admin/bookings/${bookingId}/cancel`, data),
  getCancellationHistory: () => api.get('/admin/cancellations'),
  
  // Custom Pages
  getAllPages: () => api.get('/admin/pages'),
  createPage: (data) => api.post('/admin/pages', data),
  updatePage: (id, data) => api.put(`/admin/pages/${id}`, data),
  deletePage: (id) => api.delete(`/admin/pages/${id}`),
  
  // Service Providers / Operators
  getProviderApplications: (status) => api.get('/admin/provider-applications', { params: { status } }),
  getProviderApplication: (id) => api.get(`/admin/provider-applications/${id}`),
  approveProviderApplication: (id) => api.put(`/admin/provider-applications/${id}/approve`),
  rejectProviderApplication: (id) => api.put(`/admin/provider-applications/${id}/reject`),
  deleteProviderApplication: (id) => api.delete(`/admin/provider-applications/${id}`),
  
  // Operators
  getOperators: (status) => api.get('/admin/operators', { params: { status } }),
  getOperator: (id) => api.get(`/admin/operators/${id}`),
  createOperator: (data) => api.post('/admin/operators', data),
  updateOperator: (id, data) => api.put(`/admin/operators/${id}`, data),
  deleteOperator: (id) => api.delete(`/admin/operators/${id}`),
  getOperatorProfitReport: (id, params) => api.get(`/admin/operators/${id}/profit-report`, { params }),
  sendOperatorProfitReport: (id, data) => api.post(`/admin/operators/${id}/send-profit-report`, data),
  getProfitReportHistory: () => api.get('/admin/profit-report-history'),
  
  // Referral Agents
  getReferralAgents: () => api.get('/admin/referral/agents'),
  getReferralAgent: (id) => api.get(`/admin/referral/agents/${id}`),
  
  // Service Providers (for messaging)
  getServiceProviders: () => api.get('/admin/operators'),
};

// Custom Pages API (Public)
export const pagesAPI = {
  getAll: () => api.get('/pages'),
  getBySlug: (slug) => api.get(`/pages/${slug}`),
};

// User Profile API (for logged-in users)
export const userAPI = {
  updateProfile: (data) => api.put('/user/profile', data),
  changePassword: (data) => api.put('/user/password', data),
};

// Booking API
export const bookingAPI = {
  create: (data) => api.post('/bookings/create', data),
  getStatus: (id) => api.get(`/bookings/${id}/status`),
  createCheckout: (id) => api.post(`/bookings/${id}/checkout`),
  getPickupHotels: () => api.get('/pickup-hotels'),
  getWaiver: () => api.get('/waiver'),
};

// Customer Portal API
export const customerAPI = {
  getMyBookings: () => api.get('/customer/bookings'),
  getBooking: (id) => api.get(`/customer/bookings/${id}`),
  cancelBooking: (id, reason) => api.post(`/customer/bookings/${id}/cancel`, { reason }),
  getProfile: () => api.get('/customer/profile'),
  updateProfile: (data) => api.put('/customer/profile', data),
  // Payment Methods
  getPaymentMethods: () => api.get('/customer/payment-methods'),
  createSetupIntent: () => api.post('/customer/payment-methods/setup-intent'),
  deletePaymentMethod: (id) => api.delete(`/customer/payment-methods/${id}`),
  setDefaultPaymentMethod: (id) => api.put(`/customer/payment-methods/${id}/default`),
  // Receipt - returns a blob for download
  downloadReceipt: async (bookingId) => {
    const response = await api.get(`/bookings/${bookingId}/receipt`, { responseType: 'blob' });
    return response;
  },
};

// Invoice API (Public)
export const invoiceAPI = {
  get: (id) => api.get(`/invoice/${id}`),
  createCheckout: (id) => api.post(`/invoice/${id}/checkout`),
  checkStatus: (id) => api.get(`/invoice/${id}/status`),
};

// Public Settings API
export const settingsAPI = {
  get: () => api.get('/settings'),
};

// Provider Application API (Public)
export const providerAPI = {
  submitApplication: (data) => api.post('/providers/apply', data),
  getDashboard: () => api.get('/provider/dashboard'),
};

// Referral API (Admin)
export const referralAdminAPI = {
  // Settings
  getSettings: () => api.get('/admin/referral/settings'),
  updateSettings: (data) => api.put('/admin/referral/settings', data),
  
  // Congrats Settings
  getCongratsSettings: () => api.get('/admin/referral/congrats-settings'),
  updateCongratsSettings: (data) => api.put('/admin/referral/congrats-settings', data),
  
  // Tours
  getTours: () => api.get('/admin/referral/tours'),
  toggleTourReferral: (tourId) => api.put(`/admin/referral/tours/${tourId}/toggle`),
  
  // Agents
  getAgents: () => api.get('/admin/referral/agents'),
  getAgent: (id) => api.get(`/admin/referral/agents/${id}`),
  createAgent: (data) => api.post('/admin/referral/agents', data),
  updateAgent: (id, data) => api.put(`/admin/referral/agents/${id}`, data),
  deleteAgent: (id) => api.delete(`/admin/referral/agents/${id}`),
  approveAgent: (id) => api.put(`/admin/referral/agents/${id}/approve`),
  bulkImportAgents: (data) => api.post('/admin/referral/agents/bulk-import', data),
  
  // Links
  getLinks: () => api.get('/admin/referral/links'),
  createLink: (data) => api.post('/admin/referral/links', data),
  deleteLink: (id) => api.delete(`/admin/referral/links/${id}`),
  sendLink: (data) => api.post('/admin/referral/links/send', data),
  
  // Conversions
  getConversions: () => api.get('/admin/referral/conversions'),
  updateConversionStatus: (id, status) => api.put(`/admin/referral/conversions/${id}/status?status=${status}`),
  
  // Leaderboard
  getLeaderboard: () => api.get('/admin/referral/leaderboard'),
};

// Create referral agent axios instance (uses referral_agent_token)
const referralAgentApi = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

referralAgentApi.interceptors.request.use((config) => {
  const token = localStorage.getItem('referral_agent_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Referral API (Public - for agents) - Now uses main api for unified auth
export const referralAPI = {
  getStatus: () => api.get('/referral/status'),
  apply: (data) => api.post('/referral/apply', data),
  login: (data) => api.post('/referral/login', data),
  getDashboard: () => api.get('/referral/dashboard'),
  getAvailableTours: () => api.get('/referral/available-tours'),
  trackClick: (code, tourId, source = 'link') => api.get(`/r/${code}/${tourId}?source=${source}`),
  updateBankInfo: (data) => api.put('/referral/bank-info', data),
  
  // Share Templates
  getShareTemplates: () => api.get('/referral/share-templates'),
  createShareTemplate: (data) => api.post('/referral/share-templates', data),
  updateShareTemplate: (id, data) => api.put(`/referral/share-templates/${id}`, data),
  deleteShareTemplate: (id) => api.delete(`/referral/share-templates/${id}`),
  
  // Profile
  getProfile: () => api.get('/referral/profile'),
  updateProfile: (data) => api.put('/referral/profile', data),
  
  // Public Leaderboard
  getLeaderboard: (period = 'all') => api.get(`/referral/leaderboard?period=${period}`),
};

// Create service provider axios instance (uses service_provider_token)
const serviceProviderApi = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

serviceProviderApi.interceptors.request.use((config) => {
  const token = localStorage.getItem('service_provider_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Service Provider API (Public)
export const providerPublicAPI = {
  apply: (data) => api.post('/providers/apply', data),
  login: (data) => api.post('/providers/login', data),
  getDashboard: () => serviceProviderApi.get('/providers/dashboard'),
  updateBankInfo: (data) => serviceProviderApi.put('/providers/bank-info', data),
  getCalendar: () => serviceProviderApi.get('/providers/calendar'),
  getEarnings: () => serviceProviderApi.get('/providers/earnings'),
};

// Service Provider API (Admin)
export const providerAdminAPI = {
  getAll: () => api.get('/admin/providers'),
  get: (id) => api.get(`/admin/providers/${id}`),
  create: (data) => api.post('/admin/providers', data),
  update: (id, data) => api.put(`/admin/providers/${id}`, data),
  approve: (id) => api.put(`/admin/providers/${id}/approve`),
  delete: (id) => api.delete(`/admin/providers/${id}`),
  assignTour: (tourId, providerId) => api.put(`/admin/tours/${tourId}/assign-provider?provider_id=${providerId}`),
  // Booking assignment
  assignBooking: (bookingId, data) => api.put(`/admin/bookings/${bookingId}/assign-provider`, data),
  unassignBooking: (bookingId) => api.delete(`/admin/bookings/${bookingId}/unassign-provider`),
  // Provider management
  updateCommission: (providerId, data) => api.put(`/admin/providers/${providerId}/commission`, data),
  getProviderBookings: (providerId) => api.get(`/admin/providers/${providerId}/bookings`),
  getProviderPayroll: (providerId) => api.get(`/admin/providers/${providerId}/payroll`),
  recordPayment: (providerId, data) => api.post(`/admin/providers/${providerId}/payments`, data),
  sendBookingList: (providerId, sendVia = 'email') => api.post(`/admin/providers/${providerId}/send-booking-list?send_via=${sendVia}`),
};

// Seed API
export const seedAPI = {
  seed: () => api.post('/seed'),
};

export default api;
